/* Modal de edição de UM slot de item (inventário / equipamento / armazém) — GM4 (feature edit_items).
   Reusado nas telas de conta, personagem e na página de Bots.
   - Slot ocupado: troca o item (a API mantém o serial). Slot vazio: coloca item novo.
   - A API exige o personagem (ou a conta, no armazém) OFFLINE, confere a grade do inventário/armazém
     (tamanho do item no Item.scp, sem sobrepor) e faz backup do blob (o "Desfazer último" reverte).
   - Campo do item = o mesmo do Event Manager (ItemKindField: busca, id com flags, stepper +N). */
import { useEffect, useState } from 'react';
import { X, Loader2, Save, Trash2 } from 'lucide-react';
import { ItemNamesProvider, ItemKindField } from './Events/GameItem';
import { setItemSlot, zeroItemSlots, type ItemRow } from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';

export type ItemSlotSource = 'inventory' | 'equipment' | 'warehouse';

const SOURCE_LABEL: Record<ItemSlotSource, string> = {
  inventory: 'inventário', equipment: 'equipamento', warehouse: 'armazém',
};

export function ItemSlotEditModal({
  open, source, id, slot, item, onClose, onSaved,
}: {
  open: boolean;
  source: ItemSlotSource;
  /** CharacterIdx (inventário/equipamento) ou UserNum (armazém) */
  id: number;
  slot: number;
  /** item atual do slot (null = slot vazio) */
  item: ItemRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [itemId, setItemId] = useState(0);
  const [option, setOption] = useState('0');
  const [duration, setDuration] = useState('0');
  const [extreme, setExtreme] = useState(0);
  const [divine, setDivine] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItemId(item?.itemId || 0);
    setOption(String(item?.optionData ?? 0));
    setDuration(String(item?.duration ?? 0));
    setExtreme(item?.extremeLevel ?? 0);
    setDivine(item?.divineLevel ?? 0);
  }, [open, item]);

  if (!open) return null;

  const save = async () => {
    if (!itemId) { notify('error', 'Escolha um item'); return; }
    setBusy(true);
    try {
      const res = await setItemSlot({
        source, id, slot, itemId,
        option: Number(option) || 0, duration: Number(duration) || 0,
        extreme: Math.max(0, Math.min(15, extreme)), divine: Math.max(0, Math.min(15, divine)),
      });
      notify('success', res.message || 'Slot salvo');
      onSaved();
      onClose();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar o slot');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const ok = await confirm({
      title: `Excluir o item do slot ${slot}?`,
      description: `Remove ${item.itemName || `o item #${item.itemId}`} do ${SOURCE_LABEL[source]}. `
        + 'Faz backup automático (reversível em "Desfazer último") e exige tudo offline.',
      tone: 'danger', confirmLabel: 'Excluir slot',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await zeroItemSlots(source, id, [slot], [item.itemId]);
      notify('success', res.message || 'Slot excluído');
      onSaved();
      onClose();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao excluir o slot');
    } finally {
      setBusy(false);
    }
  };

  const num = 'w-full bg-[#0a0e14] border border-white/[0.06] rounded px-2 py-1.5 text-xs text-white font-mono';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white">
            {item ? 'Editar item' : 'Colocar item'} <span className="text-white/45 font-normal">
              — {SOURCE_LABEL[source]}, slot {slot}</span>
          </h3>
          <button onClick={onClose} disabled={busy} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <ItemNamesProvider>
          <ItemKindField value={itemId} onChange={setItemId} />
        </ItemNamesProvider>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-[10px] text-white/45">Option (slots de força)</span>
            <input value={option} onChange={(e) => setOption(e.target.value.replace(/\D/g, '') || '0')} className={num} />
          </label>
          <label className="block">
            <span className="text-[10px] text-white/45">Duração (0 = permanente)</span>
            <input value={duration} onChange={(e) => setDuration(e.target.value.replace(/\D/g, '') || '0')} className={num} />
          </label>
          <label className="block">
            <span className="text-[10px] text-white/45">Extreme (0-15)</span>
            <input type="number" min={0} max={15} value={extreme}
                   onChange={(e) => setExtreme(Number(e.target.value) || 0)} className={num} />
          </label>
          <label className="block">
            <span className="text-[10px] text-white/45">Divine (0-15)</span>
            <input type="number" min={0} max={15} value={divine}
                   onChange={(e) => setDivine(Number(e.target.value) || 0)} className={num} />
          </label>
        </div>

        <p className="text-[10px] text-white/40 mt-3 leading-relaxed">
          O personagem precisa estar offline{source === 'warehouse' ? ' (todos da conta, no armazém)' : ''}.
          {source !== 'equipment' && ' O item não pode sobrepor outro na grade.'} Backup automático: "Desfazer último" reverte.
        </p>

        <div className="flex items-center gap-2 mt-4">
          {item && (
            <button onClick={remove} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-bold hover:bg-red-500/20 cursor-pointer disabled:opacity-50">
              <Trash2 size={13} /> Excluir slot
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-3 py-2 rounded-lg bg-white/[0.05] text-white/70 text-xs font-bold hover:bg-white/10 cursor-pointer">Cancelar</button>
            <button onClick={save} disabled={busy || !itemId}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/35 text-[#00e1d7] text-xs font-bold hover:bg-[#00e1d7]/25 cursor-pointer disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
